<template>
  <el-dialog
    :title="title"
    :visible.sync="dialogVisible"
    width="460px"
    append-to-body
    :close-on-click-modal="false"
    custom-class="contact-verify-dialog"
    @open="onOpen"
    @closed="reset"
  >
    <p class="verify-desc">{{ description }}</p>
    <div
      ref="track"
      class="verify-track"
      :class="{ 'is-success': passed, 'is-dragging': dragging }"
      :style="{ '--theme': themeColor }"
    >
      <div class="verify-progress" :style="{ width: offsetX + thumbSize + 'px' }" />
      <span class="verify-hint">{{ hintText }}</span>
      <div
        ref="thumb"
        class="verify-thumb"
        :style="{ transform: 'translateX(' + offsetX + 'px)' }"
        @mousedown.prevent="onPointerDown"
        @touchstart.prevent="onTouchStart"
      >
        <i :class="passed ? 'el-icon-check' : 'el-icon-d-arrow-right'" />
      </div>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button size="medium" @click="handleCancel">{{ cancelText }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
const THUMB_SIZE = 46;
const PASS_RATIO = 0.92;

export default {
  name: "ContactVerifyDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    themeColor: {
      type: String,
      default: "#024190",
    },
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    slideText: {
      type: String,
      default: "",
    },
    successText: {
      type: String,
      default: "",
    },
    cancelText: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      thumbSize: THUMB_SIZE,
      offsetX: 0,
      dragging: false,
      passed: false,
      maxOffset: 0,
      startX: 0,
      startOffset: 0,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
    hintText() {
      return this.passed ? this.successText : this.slideText;
    },
  },
  beforeDestroy() {
    this.unbindDocumentEvents();
  },
  methods: {
    onOpen() {
      this.$nextTick(() => {
        this.measureTrack();
      });
    },
    measureTrack() {
      const track = this.$refs.track;
      if (!track) {
        return;
      }
      this.maxOffset = Math.max(0, track.clientWidth - THUMB_SIZE - 4);
    },
    onPointerDown(e) {
      if (this.passed) {
        return;
      }
      this.dragging = true;
      this.startX = e.clientX;
      this.startOffset = this.offsetX;
      this.bindDocumentEvents();
    },
    onTouchStart(e) {
      if (this.passed || !e.touches.length) {
        return;
      }
      this.dragging = true;
      this.startX = e.touches[0].clientX;
      this.startOffset = this.offsetX;
      this.bindDocumentEvents(true);
    },
    bindDocumentEvents(touch) {
      if (touch) {
        document.addEventListener("touchmove", this.onTouchMove, { passive: false });
        document.addEventListener("touchend", this.onTouchEnd);
      } else {
        document.addEventListener("mousemove", this.onPointerMove);
        document.addEventListener("mouseup", this.onPointerUp);
      }
    },
    unbindDocumentEvents() {
      document.removeEventListener("mousemove", this.onPointerMove);
      document.removeEventListener("mouseup", this.onPointerUp);
      document.removeEventListener("touchmove", this.onTouchMove);
      document.removeEventListener("touchend", this.onTouchEnd);
    },
    clampOffset(value) {
      return Math.min(Math.max(0, value), this.maxOffset);
    },
    onPointerMove(e) {
      if (!this.dragging) {
        return;
      }
      const delta = e.clientX - this.startX;
      this.offsetX = this.clampOffset(this.startOffset + delta);
    },
    onTouchMove(e) {
      if (!this.dragging || !e.touches.length) {
        return;
      }
      e.preventDefault();
      const delta = e.touches[0].clientX - this.startX;
      this.offsetX = this.clampOffset(this.startOffset + delta);
    },
    finishDrag() {
      if (!this.dragging) {
        return;
      }
      this.dragging = false;
      this.unbindDocumentEvents();
      if (this.maxOffset > 0 && this.offsetX >= this.maxOffset * PASS_RATIO) {
        this.offsetX = this.maxOffset;
        this.passed = true;
        this.$emit("success");
        setTimeout(() => {
          this.dialogVisible = false;
        }, 350);
      } else {
        this.offsetX = 0;
      }
    },
    onPointerUp() {
      this.finishDrag();
    },
    onTouchEnd() {
      this.finishDrag();
    },
    reset() {
      this.offsetX = 0;
      this.passed = false;
      this.dragging = false;
      this.unbindDocumentEvents();
    },
    handleCancel() {
      this.dialogVisible = false;
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss" scoped>
.verify-desc {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.6;
  color: #606266;
}

.verify-track {
  position: relative;
  height: 48px;
  border-radius: 24px;
  background: #f0f2f5;
  border: 1px solid #e4e7ed;
  overflow: hidden;
  user-select: none;
  touch-action: none;

  &.is-success {
    border-color: var(--theme);
    background: #f0f6ff;
  }
}

.verify-progress {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  max-width: 100%;
  background: linear-gradient(
    90deg,
    rgba(2, 65, 144, 0.12),
    rgba(2, 65, 144, 0.32)
  );
  border-radius: 24px 0 0 24px;
  pointer-events: none;
  transition: width 0.05s linear;
}

.verify-track.is-success .verify-progress {
  background: linear-gradient(
    90deg,
    rgba(2, 65, 144, 0.22),
    rgba(2, 65, 144, 0.5)
  );
}

.verify-hint {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #909399;
  pointer-events: none;
  padding-left: 40px;
}

.verify-track.is-success .verify-hint {
  color: var(--theme);
  font-weight: 600;
}

.verify-thumb {
  position: absolute;
  left: 2px;
  top: 2px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  z-index: 2;
  color: var(--theme);
  font-size: 18px;
  font-weight: bold;
  transition: background 0.2s;

  &:active {
    cursor: grabbing;
  }
}

.verify-track.is-success .verify-thumb {
  background: var(--theme);
  color: #fff;
}
</style>

<style lang="scss">
.contact-verify-dialog {
  border-radius: 4px;

  .el-dialog__header {
    padding: 16px 20px 10px;
    border-bottom: 1px solid #e8eaec;

    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
  }

  .el-dialog__body {
    padding: 20px;
  }

  .el-dialog__footer {
    padding: 12px 20px 16px;
    border-top: 1px solid #e8eaec;
    background: #fafafa;
  }

  .dialog-footer {
    text-align: right;

    .el-button {
      min-width: 88px;
      padding: 18px 20px;
      font-size: 16px;
      font-weight: bolder;
      color: #fff !important;
      background-color: #024190; 
      border: none
    }
  }
}
</style>
